import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useResearch } from '../../hooks/useResearch';
import { researchService } from '../../services/dataService';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, Edit2, Trash2, ExternalLink } from 'lucide-react';

const AdminResearch = () => {
  const navigate = useNavigate();
  const { research, loading, error, refetch } = useResearch();
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (id, title) => {
    if (window.confirm(`Delete "${title}"? This cannot be undone.`)) {
      setDeleting(id);
      try {
        const result = await researchService.delete(id);
        if (result.success) {
          toast.success('Research deleted successfully');
          refetch();
        } else {
          toast.error('Failed to delete research');
        }
      } catch (error) {
        toast.error('Error deleting research');
      } finally {
        setDeleting(null);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Published':
        return 'bg-green-500/20 text-green-400';
      case 'Under Review':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'In Progress':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-white/10 text-white/50';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/dashboard')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <button
            onClick={() => navigate('/adminaccess/research/new')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Research</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-light mb-2">Manage Research</h1>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="p-8 rounded-lg border border-white/10 bg-white/5 text-center">
            <div className="inline-block w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
            <p className="text-white/60">Loading research...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-8 rounded-lg border border-red-500/20 bg-red-500/5 text-center">
            <p className="text-red-400">Error: {error}</p>
          </div>
        )}

        {/* Research List */}
        {!loading && !error && (
          <div className="space-y-4">
            {research.length === 0 ? (
              <div className="p-12 rounded-lg border border-white/10 bg-white/5 text-center">
                <p className="text-white/40 mb-4">No research publications yet</p>
                <button
                  onClick={() => navigate('/adminaccess/research/new')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Your First Publication</span>
                </button>
              </div>
            ) : (
              research.map((item) => (
                <div
                  key={item.id}
                  className="p-5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-medium">{item.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 text-sm text-white/40 mb-3">
                        <span>{item.venue}</span>
                        <span>•</span>
                        <span>{item.type}</span>
                        <span>•</span>
                        <span>{item.date}</span>
                      </div>

                      <p className="text-sm text-white/60 mb-3 line-clamp-2">
                        {item.description}
                      </p>

                      <div className="text-xs text-white/40 mb-2">
                        <strong>Authors:</strong> {item.authors}
                      </div>

                      {item.link && item.link !== '#' && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          View Publication
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => navigate(`/adminaccess/research/edit/${item.id}`)}
                        className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id, item.title)}
                        disabled={deleting === item.id}
                        className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminResearch;